import { useCallback, useMemo } from "react"

import { canExpandGroup } from "./canExpandGroup"
import { getColumnId } from "./getColumnId"
import { getVisibleColumns } from "./getVisibleColumns"
import {
  ColumnType,
  ColumnWidths,
  DataColumn,
  GroupColumn,
  RowReorderingMode,
  UseTableHeaderProps,
} from "./types"
import {
  HeaderCellType,
  HeaderGroupRowCell,
  HeaderRowCell,
  HeaderRowHeaderCell,
  TableHeader,
} from "../Table"
import { HeaderGroupCellType, HeaderGroupRow, HeaderRow } from "../Table/types"
import { TableCellWidth } from "../TableCell"
import { defaultWidths } from "./useTableColumnWidths"

export const groupRowCheckboxEmptyKey = "group-row-checkbox-empty"

export function useTableHeader<T>({
  data,
  columns,
  columnWidths,
  rowSelectionKind,
  canSelectAllRows,
  state,
  dispatch,
  hiddenColumns,
  toggleColumnHideShow,
  enableColumnReordering,
  reorderColumn,
  rowReorderingMode = RowReorderingMode.Hidden,
  startColumnResize,
  hideColumnBorders,
}: UseTableHeaderProps<T>): TableHeader {
  const showDragHandleColumn = rowReorderingMode !== RowReorderingMode.Hidden

  const groupRow = useMemo<HeaderGroupRow>(() => {
    const hasGroupColumns = columns.some((col) => col.type === ColumnType.Group)
    if (!hasGroupColumns) return { cells: [] }
    const cells: HeaderGroupRowCell[] = []
    for (const col of columns) {
      if (col.type === ColumnType.Group) {
        const visibleSubColumns = getVisibleColumns(
          col,
          state.expandedGroups,
          hiddenColumns
        )

        if (visibleSubColumns.length === 0) {
          continue
        }
        cells.push({
          key: col.title,
          type: HeaderGroupCellType.Group,
          title: col.renderTitle?.(col.title) ?? col.title,
          accessory: col.accessory,
          colSpan: visibleSubColumns.length,
          isExpandable: canExpandGroup(col),
          isExpanded: state.expandedGroups.has(col.title),
          onToggleExpanded: () => {
            dispatch({
              type: "toggle_group_expanded",
              groupTitle: col.title,
            })
          },
        })
      } else {
        if (!hiddenColumns?.has(getColumnId(col))) {
          cells.push({
            key: `empty-${col.title}`,
            type: HeaderGroupCellType.Empty,
            colSpan: 1,
          })
        }
      }
    }
    if (data.length > 0 && !!rowSelectionKind && cells.length > 0) {
      cells.unshift({
        key: groupRowCheckboxEmptyKey,
        type: HeaderGroupCellType.Empty,
        colSpan: 1,
      })
    }
    if (data.length > 0 && showDragHandleColumn && cells.length > 0) {
      cells.unshift({
        key: "group-row-drag-handle-empty",
        type: HeaderGroupCellType.Empty,
        colSpan: 1,
      })
    }
    return { cells }
  }, [
    rowSelectionKind,
    columns,
    data,
    state.expandedGroups,
    hiddenColumns,
    dispatch,
    showDragHandleColumn,
  ])

  const mapToHeaderCells = useCallback<
    (group: GroupColumn<T>) => HeaderRowCell[]
  >(
    (group) =>
      getVisibleColumns(group, state.expandedGroups, hiddenColumns).map((col) =>
        mapToHeaderCell(col, columnWidths, group, toggleColumnHideShow)
      ),
    [state.expandedGroups, hiddenColumns, toggleColumnHideShow, columnWidths]
  )

  const row = useMemo<HeaderRow>(() => {
    const cells: HeaderRowCell[] = columns.flatMap((col) => {
      if (col.type === ColumnType.Group) {
        return mapToHeaderCells(col)
      }
      if (hiddenColumns?.has(getColumnId(col))) {
        return []
      }
      return [
        mapToHeaderCell(col, columnWidths, undefined, toggleColumnHideShow),
      ]
    })
    if (data.length > 0 && !!rowSelectionKind && cells.length > 0) {
      if (canSelectAllRows) {
        cells.unshift({
          key: "header-checkbox",
          type: HeaderCellType.Checkbox,
          isChecked: state.isAllSelected,
          onChangeIsChecked: () => {
            dispatch({ type: "toggle_all_selected" })
          },
          width: defaultWidths[TableCellWidth.ExtraExtraSmall],
        })
      } else {
        cells.unshift({
          key: "header-checkbox-empty",
          type: HeaderCellType.Empty,
          width: defaultWidths[TableCellWidth.ExtraExtraSmall],
        })
      }
    }
    if (data.length > 0 && showDragHandleColumn && cells.length > 0) {
      cells.unshift({
        key: "header-drag-handle-empty",
        type: HeaderCellType.Empty,
        width: defaultWidths[TableCellWidth.ExtraExtraSmall],
      })
    }
    return { cells }
  }, [
    rowSelectionKind,
    canSelectAllRows,
    columns,
    data,
    dispatch,
    mapToHeaderCells,
    state.isAllSelected,
    hiddenColumns,
    toggleColumnHideShow,
    showDragHandleColumn,
    columnWidths,
  ])

  return {
    groupRow,
    row,
    onReorder: reorderColumn,
    enableColumnReordering,
    onStartColumnResize: startColumnResize,
    hideColumnBorders,
  }
}

function mapToHeaderCell<T>(
  col: DataColumn<T>,
  columnWidths: ColumnWidths,
  group?: GroupColumn<T>,
  toggleColumnHideShow?: (columnTitle: string) => void
): HeaderRowHeaderCell {
  const key = getColumnId(col, group)
  return {
    key,
    type: HeaderCellType.Header,
    content: col.renderTitle?.(col.title) ?? col.title,
    width: columnWidths[key],
    onToggleColumnHideShow: toggleColumnHideShow
      ? () => toggleColumnHideShow(getColumnId(col, group))
      : undefined,
  }
}
